import React, { useEffect } from 'react';
import { Fragment } from 'react';
import { FormFieldTypes, formFieldsConfig } from '../config/form-field-config';
import { StringComparisonCriteria, isFunction } from '@r-and-a-shared-ui/utils';
import { MenuPlacement } from 'react-select';
import { RichTextWithAsset } from 'libs/common-kontent-client/src/models/SSO';
import { PPFormFieldProps } from '../FormDropdownPP/FormDropdownPP';
import { DictionaryWithLinkedItemProps } from '../../types';
import { useFormContext } from 'react-hook-form';
import { shouldHideFieldByDependency } from '../../helpers/field-visibility-helper';

export interface OptionProps {
  gender?: string;
  label?: string;
  value?: string;
  counties?: OptionProps[];
  states?: OptionProps[];
  isDisabled?: boolean;
  fontAwesomeIcon?: string;
}

export interface RegexValidationProps {
  regex?: string;
  validationMessage?: string;
}

export interface BaseFormFieldProps {
  defaultValue?: string;
  key: string;
  name: string;
  queryParamKey?: string;
  fieldRef: React.RefObject<HTMLDivElement>;
  placeholder?: string;
  label?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  validationMessage?: string;
  validationRegex?: string;
  hint?: string;
  fieldType: FormFieldTypes;
  options?: OptionProps[];
  nonEmptyFields?: string[];
  defaultOption?: OptionProps;
  defaultOptionMessage?: string;
  dependsOn?: string;
  dependencyCriteria?: StringComparisonCriteria;
  dependencyValidationMessage?: string;
  subfields?: FormFieldProps[];
  allowNumbersOnly?: boolean;
  hideIndividualError?: boolean;
  triggerError?: boolean;
  preferredOptionsList?: string;
  entireObjectSelection?: boolean;
  menuPlacement?: MenuPlacement;
  fieldReplacement?: RichTextWithAsset;
  isHidden?: boolean;
  isMembership?: boolean;
  isSubmittedSuccessfully?: boolean;
  onChange?: (value: any) => void;
  value?: any;
  hiddenClearable?: boolean;
  hideIcon?: boolean;
  multipleValidation?: RegexValidationProps[];
  multipleValidationLabel?: string;
  numberedOptionsEnabled?: boolean;
  displayHideFieldBasedOnOtherFieldsValue?: DictionaryWithLinkedItemProps[];
  disableEnterSubmit?: boolean;
}

export type FormFieldProps = BaseFormFieldProps;

export const FormField = (props: FormFieldProps | PPFormFieldProps) => {
  const { fieldType, isMembership, isSubmittedSuccessfully, isHidden } = props;
  const { watch, setValue } = useFormContext();
  const watchedValues = watch();
  const hideByFieldRule = shouldHideFieldByDependency(
    props as FormFieldProps,
    watchedValues,
  );

  const fieldName = (props as FormFieldProps).name;
  const defaultValue = (props as FormFieldProps).defaultValue;
  const shouldFieldBeHidden = isHidden || hideByFieldRule;

  useEffect(() => {
    if (shouldFieldBeHidden && fieldName) {
      setValue(fieldName, defaultValue);
    }
  }, [shouldFieldBeHidden, fieldName, defaultValue, setValue]);

  if (!isFunction(formFieldsConfig[fieldType])) {
    return undefined;
  }

  return (
    <Fragment>
      {formFieldsConfig[fieldType]({
        ...props,
        isMembership,
        isSubmittedSuccessfully,
        isHidden: shouldFieldBeHidden,
      })}
    </Fragment>
  );
};

export default FormField;
